<template>
  <div class="flex mt-8 bg-surface items-center rounded-xl py-4">
    <div
      v-if="!selectedCategory || subcategoryButtons.length === 0"
      class="hidden md:flex flex-wrap gap-2 px-4 border-r flex-1"
    >
      <Btn
        v-for="categoryButton in categoryButtons"
        :key="categoryButton.label"
        :size="BtnSize.LG"
        :color="BtnColor.TERTIARY"
        :variant="
          selectedCategory === categoryButton.category
            ? BtnVariant.FILLED
            : BtnVariant.OUTLINED
        "
        :aria-selected="
          (selectedCategory === categoryButton.category).toString()
        "
        @click="setCategoryFilter(categoryButton.category)"
      >
        {{ categoryButton.label }}
      </Btn>
    </div>
    <div v-else class="hidden md:flex flex-wrap gap-2 px-4 border-r flex-1">
      <div class="w-full flex items-center">
        <Btn
          :color="BtnColor.SECONDARY"
          icon
          aria-label="Back to categories filter"
          @click="setCategoryFilter(null)"
        >
          <Icon :icon="ArrowLeftCircleSvg" />
        </Btn>
        <span class="ml-2 text-on-surface-variant">
          {{ selectedCategory.name }}
        </span>
      </div>

      <Btn
        v-for="subcategoryButton in subcategoryButtons"
        :key="subcategoryButton.label"
        class="whitespace-nowrap"
        :color="BtnColor.TERTIARY"
        :variant="
          subcategoryButton.value === filters.subcategoryId
            ? BtnVariant.FILLED
            : BtnVariant.OUTLINED
        "
        :aria-selected="
          (subcategoryButton.value === filters.subcategoryId).toString()
        "
        @click="filters.subcategoryId = subcategoryButton.value"
      >
        {{ subcategoryButton.label }}
      </Btn>
    </div>

    <div class="flex md:flex-row-reverse gap-2 px-3 w-full md:w-1/3">
      <SearchInput
        v-model="filters.search"
        class="flex-1"
        :debounce="DEBOUNCE_DELAY_MS"
        hide-details
      />
      <FiltersModal v-model="filters" :hub="props.hub" />
    </div>
  </div>
</template>

<script setup lang="ts">
import ArrowLeftCircleSvg from '@/assets/icons/arrow-left-circle.svg?component';
import { HubDto } from '~/api/hubs';
import { Btn, BtnColor, BtnVariant, BtnSize } from '~/components/btn';
import { Icon } from '~/components/icon';
import { SearchInput } from '~/components/inputs/search-input';
import { FiltersModal } from '../filters-modal';
import { HubFilters } from '../../_types';

type Props = {
  hub: HubDto;
};

const props = defineProps<Props>();
const filters = defineModel<HubFilters>({ required: true });

const { t } = useI18n();

const selectedCategory = computed<null | HubDto['categories'][number]>(() => {
  if (filters.value.categoryId === null) {
    return null;
  }

  const category = props.hub.categories.find(
    (category) => category.id === filters.value.categoryId,
  );

  return category ?? null;
});

const categoryButtons = computed(() => {
  if (!props.hub) {
    return [];
  }
  return [
    { category: null, label: t('filters-modal.all') },
    ...props.hub.categories.map((category) => ({
      category,
      label: category.name,
    })),
  ];
});
const subcategoryButtons = computed(() => {
  if (
    selectedCategory.value === null ||
    selectedCategory.value.subcategories.length === 0
  ) {
    return [];
  }
  return [
    { value: null, label: 'All' },
    ...selectedCategory.value.subcategories.map((subcategory) => ({
      value: subcategory.id,
      label: subcategory.name,
    })),
  ];
});
const setCategoryFilter = (category: null | HubDto['categories'][number]) => {
  filters.value.categoryId = category?.id ?? null;
  filters.value.subcategoryId = null;
};
</script>
